import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, OG_IMAGE } from "../lib/seo";

const LAST_UPDATED = "February 2026";
const CONTACT_EMAIL = "hello@refinetouch.com";

function LegalShell({ title, children }) {
  const { t } = useI18n();
  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="legal-page">
      <div className="text-xs eyebrow text-stone-500 mb-3">
        {t("last_updated")} — {LAST_UPDATED}
      </div>
      <h1 className="font-serif text-5xl leading-tight mb-8">{title}</h1>
      <div className="prose prose-stone max-w-none text-stone-700 leading-relaxed space-y-6 text-[15px]">
        {children}
        <hr className="my-10 border-stone-200" />
        <p className="text-sm text-stone-500">
          {t("contact_us_at")}{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <p className="text-sm">
          <Link to="/" className="underline">← Refine Touch</Link>
        </p>
      </div>
    </div>
  );
}

/* -------------------- TERMS OF SERVICE -------------------- */

export function TermsPage() {
  const { lang, t } = useI18n();
  useSEO({
    title: "Terms of Service | Refine Touch USA",
    description: "Terms of Service for Refine Touch USA — the marketplace connecting U.S. clients with French professionals.",
    canonical: `${SITE_URL}/terms`,
    ogImage: OG_IMAGE,
  });

  const EN = (
    <>
      <p><strong>Welcome to Refine Touch.</strong> These Terms of Service ("Terms") govern your use of the Refine Touch platform, website and services (collectively, the "Platform"), which connects customers ("Customers") with independent French-speaking service providers ("Providers") in the United States. By creating an account or booking a service you agree to these Terms.</p>

      <h2 className="font-serif text-2xl mt-8">1. About Refine Touch</h2>
      <p>Refine Touch operates an online marketplace. We do not employ Providers and we do not perform the services listed on the Platform. Each Provider is an independent contractor solely responsible for the services they deliver.</p>

      <h2 className="font-serif text-2xl mt-8">2. Eligibility & accounts</h2>
      <p>You must be at least 18 years old and legally able to enter into a contract to use the Platform. You are responsible for the accuracy of the information in your profile and for keeping your credentials confidential. Providers must additionally hold any licenses required in their city and category (e.g., real-estate broker license, cosmetology license, food-handling permit).</p>

      <h2 className="font-serif text-2xl mt-8">3. Bookings & payments</h2>
      <p>When a Customer books a Provider through the Platform, a direct service contract is formed between the Customer and the Provider. Refine Touch acts as a limited payment agent to collect the price on behalf of the Provider. Prices are displayed in US dollars unless otherwise indicated.</p>
      <p>Refine Touch charges a platform commission of 15% of the total service price. The remaining 85% is remitted to the Provider after the service is completed and the cancellation window has elapsed.</p>

      <h2 className="font-serif text-2xl mt-8">4. Cancellations & refunds</h2>
      <p>Unless the Provider offers a stricter policy on their listing, Customers may cancel a booking free of charge up to 24 hours before the scheduled start time. Late cancellations may be charged 50% of the price; no-shows may be charged 100%. Refunds are processed to the original payment method within 10 business days.</p>

      <h2 className="font-serif text-2xl mt-8">5. Provider responsibilities</h2>
      <p>Providers agree to (a) deliver services with reasonable skill and care, (b) comply with all applicable federal, state and local laws, (c) carry appropriate insurance for their category, (d) not solicit Customers off-platform to circumvent commissions, and (e) accurately describe their services and pricing.</p>

      <h2 className="font-serif text-2xl mt-8">6. Prohibited conduct</h2>
      <p>You agree not to use the Platform to (i) post false, misleading, or unlawful content, (ii) infringe any intellectual property, (iii) harass or discriminate against any user, (iv) attempt to circumvent our fees, or (v) interfere with the operation of the Platform.</p>

      <h2 className="font-serif text-2xl mt-8">7. Reviews</h2>
      <p>Reviews may only be submitted by Customers who have completed a booking. Reviews must reflect the reviewer's genuine experience. Refine Touch may remove reviews that violate these Terms.</p>

      <h2 className="font-serif text-2xl mt-8">8. Intellectual property</h2>
      <p>The Refine Touch name, logo, and platform are our exclusive property. Content posted by users remains theirs, but they grant Refine Touch a worldwide, royalty-free license to display it as needed to operate the Platform.</p>

      <h2 className="font-serif text-2xl mt-8">9. Disclaimers & limitation of liability</h2>
      <p>The Platform is provided "as is". Refine Touch does not warrant the quality, safety, or legality of services offered by Providers. To the maximum extent permitted by law, our aggregate liability arising out of these Terms is limited to the platform commission collected on your booking during the six months preceding the claim.</p>

      <h2 className="font-serif text-2xl mt-8">10. Termination</h2>
      <p>We may suspend or terminate any account that breaches these Terms. You may close your account at any time by writing to us at the address below.</p>

      <h2 className="font-serif text-2xl mt-8">11. Governing law</h2>
      <p>These Terms are governed by the laws of the State of Florida, USA. Any dispute will be brought before the state or federal courts located in Miami-Dade County, Florida, and you consent to their exclusive jurisdiction.</p>

      <h2 className="font-serif text-2xl mt-8">12. Changes</h2>
      <p>We may update these Terms from time to time. Material changes will be announced by email or on the Platform at least 14 days before they take effect.</p>
    </>
  );

  const FR = (
    <>
      <p><strong>Bienvenue sur Refine Touch.</strong> Les présentes Conditions d'utilisation (« Conditions ») régissent votre utilisation de la plateforme Refine Touch, du site web et des services (collectivement, la « Plateforme »), qui met en relation des clients (« Clients ») avec des prestataires francophones indépendants (« Prestataires ») aux États-Unis. En créant un compte ou en réservant un service, vous acceptez ces Conditions.</p>

      <h2 className="font-serif text-2xl mt-8">1. À propos de Refine Touch</h2>
      <p>Refine Touch exploite une place de marché en ligne. Nous n'employons pas les Prestataires et n'exécutons pas les services listés sur la Plateforme. Chaque Prestataire est un contractant indépendant, seul responsable des services qu'il fournit.</p>

      <h2 className="font-serif text-2xl mt-8">2. Éligibilité & comptes</h2>
      <p>Vous devez avoir au moins 18 ans et la capacité juridique de contracter pour utiliser la Plateforme. Vous êtes responsable de l'exactitude des informations de votre profil et de la confidentialité de vos identifiants. Les Prestataires doivent en outre détenir toute licence requise dans leur ville et catégorie (agent immobilier, esthétique, hygiène alimentaire, etc.).</p>

      <h2 className="font-serif text-2xl mt-8">3. Réservations & paiements</h2>
      <p>Lorsqu'un Client réserve un Prestataire via la Plateforme, un contrat de service direct est conclu entre le Client et le Prestataire. Refine Touch agit comme agent de paiement limité pour collecter le prix pour le compte du Prestataire. Les prix sont affichés en dollars américains sauf indication contraire.</p>
      <p>Refine Touch prélève une commission de 15 % du prix total du service. Les 85 % restants sont reversés au Prestataire après réalisation de la prestation et expiration du délai d'annulation.</p>

      <h2 className="font-serif text-2xl mt-8">4. Annulations & remboursements</h2>
      <p>Sauf politique plus stricte publiée par le Prestataire, les Clients peuvent annuler gratuitement jusqu'à 24 h avant le début de la prestation. Les annulations tardives peuvent être facturées à 50 % ; les no-shows à 100 %. Les remboursements sont émis vers le moyen de paiement d'origine sous 10 jours ouvrés.</p>

      <h2 className="font-serif text-2xl mt-8">5. Obligations des Prestataires</h2>
      <p>Les Prestataires s'engagent à (a) fournir leurs services avec compétence et diligence, (b) respecter toutes les lois fédérales, d'État et locales applicables, (c) souscrire une assurance adaptée à leur catégorie, (d) ne pas démarcher les Clients hors plateforme pour contourner les commissions et (e) décrire fidèlement leurs services et tarifs.</p>

      <h2 className="font-serif text-2xl mt-8">6. Conduites interdites</h2>
      <p>Vous vous engagez à ne pas utiliser la Plateforme pour (i) publier des contenus faux, trompeurs ou illicites, (ii) enfreindre une propriété intellectuelle, (iii) harceler ou discriminer un autre utilisateur, (iv) contourner nos frais ou (v) perturber le fonctionnement de la Plateforme.</p>

      <h2 className="font-serif text-2xl mt-8">7. Avis</h2>
      <p>Seuls les Clients ayant réalisé une prestation peuvent laisser un avis. Les avis doivent refléter une expérience authentique. Refine Touch peut supprimer les avis qui enfreignent ces Conditions.</p>

      <h2 className="font-serif text-2xl mt-8">8. Propriété intellectuelle</h2>
      <p>Le nom, le logo et la plateforme Refine Touch sont notre propriété exclusive. Les contenus publiés par les utilisateurs leur appartiennent, mais ils accordent à Refine Touch une licence mondiale et gratuite pour les afficher dans le cadre de l'exploitation de la Plateforme.</p>

      <h2 className="font-serif text-2xl mt-8">9. Garanties & limitation de responsabilité</h2>
      <p>La Plateforme est fournie « en l'état ». Refine Touch ne garantit ni la qualité, ni la sécurité, ni la légalité des services proposés par les Prestataires. Dans toute la mesure permise par la loi, notre responsabilité globale au titre des présentes est limitée à la commission perçue sur votre réservation au cours des six mois précédant la réclamation.</p>

      <h2 className="font-serif text-2xl mt-8">10. Résiliation</h2>
      <p>Nous pouvons suspendre ou résilier tout compte enfreignant ces Conditions. Vous pouvez fermer votre compte à tout moment en nous écrivant à l'adresse ci-dessous.</p>

      <h2 className="font-serif text-2xl mt-8">11. Droit applicable</h2>
      <p>Les présentes Conditions sont régies par les lois de l'État de Floride, USA. Tout litige sera porté devant les tribunaux étatiques ou fédéraux du comté de Miami-Dade, Floride, dont vous acceptez la compétence exclusive.</p>

      <h2 className="font-serif text-2xl mt-8">12. Modifications</h2>
      <p>Nous pouvons modifier ces Conditions. Toute modification substantielle sera annoncée par email ou sur la Plateforme au moins 14 jours avant sa prise d'effet.</p>
    </>
  );

  return <LegalShell title={t("legal_terms")}>{lang === "fr" ? FR : EN}</LegalShell>;
}

/* -------------------- PRIVACY POLICY -------------------- */

export function PrivacyPage() {
  const { lang, t } = useI18n();
  useSEO({
    title: "Privacy Policy | Refine Touch USA",
    description: "Privacy Policy for Refine Touch USA — how we collect, use and protect the data of U.S. clients and French providers.",
    canonical: `${SITE_URL}/privacy`,
    ogImage: OG_IMAGE,
  });

  const EN = (
    <>
      <p>This Privacy Policy explains how <strong>Refine Touch</strong> ("we", "us") collects, uses and shares personal data when you use the Platform. We comply with US federal law and applicable state privacy laws (including the California Consumer Privacy Act, "CCPA") and, when we serve users in the European Union, with the GDPR.</p>

      <h2 className="font-serif text-2xl mt-8">1. Data we collect</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Account data</strong> — name, email, phone, city, role (Customer / Provider), password hash.</li>
        <li><strong>Profile data</strong> — bio, skills, portfolio images, years of experience, avatar.</li>
        <li><strong>Booking data</strong> — services booked, dates, notes, price, commission, payment status.</li>
        <li><strong>Payment data</strong> — handled directly by our payment processor (Stripe). We do not store your full card number on our servers.</li>
        <li><strong>Usage data</strong> — IP address, device info, pages visited, cookies (see our Cookie Policy).</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">2. How we use it</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>To create and operate your account.</li>
        <li>To match Customers with Providers and process bookings.</li>
        <li>To process payments and remit earnings to Providers.</li>
        <li>To keep the Platform safe and prevent fraud.</li>
        <li>To send transactional emails (booking confirmations, receipts, cancellations).</li>
        <li>To send marketing emails only when you have opted in — you can opt out at any time.</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">3. Legal bases (EEA / UK users)</h2>
      <p>We rely on: performance of the contract between you and us, our legitimate interests in operating and improving the Platform, your consent (for marketing and non-essential cookies), and compliance with legal obligations.</p>

      <h2 className="font-serif text-2xl mt-8">4. Sharing</h2>
      <p>We share personal data with (a) the counter-party of your booking (Customer ↔ Provider), (b) trusted service providers (hosting, payment processing, email delivery, analytics), (c) government authorities when required by law. We do not sell your personal data.</p>

      <h2 className="font-serif text-2xl mt-8">5. Retention</h2>
      <p>We keep account data for as long as your account is active. Booking and financial records are kept for up to 7 years to meet tax and accounting obligations. You may request earlier deletion, subject to legal retention duties.</p>

      <h2 className="font-serif text-2xl mt-8">6. Your rights</h2>
      <p>Depending on where you live, you may have the right to access, correct, delete, restrict or port your data, and to object to certain processing. California residents have specific CCPA rights, including the right to know what we collect and to opt out of the "sale" or "sharing" of personal information (we do not sell data). To exercise these rights, email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.</p>

      <h2 className="font-serif text-2xl mt-8">7. Security</h2>
      <p>We use encryption in transit (TLS), hashed passwords (bcrypt), access controls and continuous monitoring. No system is 100% secure — please protect your credentials.</p>

      <h2 className="font-serif text-2xl mt-8">8. International transfers</h2>
      <p>Our servers are located in the United States. If you access the Platform from another country, your data will be transferred to the US, subject to appropriate safeguards.</p>

      <h2 className="font-serif text-2xl mt-8">9. Children</h2>
      <p>The Platform is not intended for anyone under 18. We do not knowingly collect personal data from minors.</p>

      <h2 className="font-serif text-2xl mt-8">10. Changes</h2>
      <p>We may update this Policy. Material changes will be posted here and, where required, notified by email.</p>
    </>
  );

  const FR = (
    <>
      <p>La présente Politique de confidentialité explique comment <strong>Refine Touch</strong> (« nous ») collecte, utilise et partage les données personnelles lorsque vous utilisez la Plateforme. Nous respectons le droit fédéral américain et les lois d'État applicables en matière de confidentialité (notamment le CCPA en Californie) et, lorsque nous servons des utilisateurs de l'Union européenne, le RGPD.</p>

      <h2 className="font-serif text-2xl mt-8">1. Données que nous collectons</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Compte</strong> — nom, email, téléphone, ville, rôle (Client / Prestataire), hash du mot de passe.</li>
        <li><strong>Profil</strong> — bio, compétences, portfolio, années d'expérience, avatar.</li>
        <li><strong>Réservation</strong> — services réservés, dates, notes, prix, commission, statut de paiement.</li>
        <li><strong>Paiement</strong> — traité directement par notre prestataire (Stripe). Nous ne stockons pas le numéro complet de carte.</li>
        <li><strong>Usage</strong> — adresse IP, appareil, pages visitées, cookies (voir la Politique cookies).</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">2. Utilisation des données</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Créer et exploiter votre compte.</li>
        <li>Mettre en relation Clients et Prestataires et traiter les réservations.</li>
        <li>Traiter les paiements et reverser les revenus aux Prestataires.</li>
        <li>Assurer la sécurité de la Plateforme et prévenir la fraude.</li>
        <li>Envoyer des emails transactionnels (confirmations, reçus, annulations).</li>
        <li>Envoyer des emails marketing uniquement avec votre consentement — vous pouvez vous désinscrire à tout moment.</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">3. Bases légales (EEE / UK)</h2>
      <p>Nous nous fondons sur : l'exécution du contrat qui nous lie, nos intérêts légitimes à opérer et améliorer la Plateforme, votre consentement (pour le marketing et les cookies non essentiels) et le respect de nos obligations légales.</p>

      <h2 className="font-serif text-2xl mt-8">4. Partage</h2>
      <p>Nous partageons des données avec (a) la contrepartie de votre réservation, (b) des sous-traitants de confiance (hébergement, paiements, envoi d'emails, analytics), (c) les autorités lorsque la loi l'exige. Nous ne vendons pas vos données.</p>

      <h2 className="font-serif text-2xl mt-8">5. Conservation</h2>
      <p>Nous conservons les données de compte tant qu'il est actif. Les registres de réservation et financiers peuvent être conservés jusqu'à 7 ans pour respecter nos obligations fiscales et comptables.</p>

      <h2 className="font-serif text-2xl mt-8">6. Vos droits</h2>
      <p>Selon votre lieu de résidence, vous pouvez disposer des droits d'accès, rectification, suppression, limitation, portabilité et opposition. Pour exercer ces droits, écrivez à <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.</p>

      <h2 className="font-serif text-2xl mt-8">7. Sécurité</h2>
      <p>Nous utilisons le chiffrement en transit (TLS), le hachage des mots de passe (bcrypt), des contrôles d'accès et un monitoring continu.</p>

      <h2 className="font-serif text-2xl mt-8">8. Transferts internationaux</h2>
      <p>Nos serveurs sont situés aux États-Unis. Si vous accédez à la Plateforme depuis un autre pays, vos données seront transférées aux USA, sous garanties appropriées.</p>

      <h2 className="font-serif text-2xl mt-8">9. Enfants</h2>
      <p>La Plateforme n'est pas destinée aux personnes de moins de 18 ans. Nous ne collectons pas sciemment de données auprès de mineurs.</p>

      <h2 className="font-serif text-2xl mt-8">10. Modifications</h2>
      <p>Nous pouvons mettre à jour cette Politique. Les changements substantiels seront publiés ici et, si nécessaire, notifiés par email.</p>
    </>
  );

  return <LegalShell title={t("legal_privacy")}>{lang === "fr" ? FR : EN}</LegalShell>;
}

/* -------------------- COOKIE POLICY -------------------- */

export function CookiesPage() {
  const { lang, t } = useI18n();
  useSEO({
    title: "Cookie Policy | Refine Touch USA",
    description: "Cookie Policy for Refine Touch USA — the cookies we use, why, and how you can manage them.",
    canonical: `${SITE_URL}/cookies`,
    ogImage: OG_IMAGE,
  });

  const EN = (
    <>
      <p>This Cookie Policy explains how <strong>Refine Touch</strong> uses cookies and similar technologies on the Platform.</p>

      <h2 className="font-serif text-2xl mt-8">1. What are cookies?</h2>
      <p>Cookies are small text files placed on your device when you visit a website. They help the site remember information (such as your login or language preference) between pages and visits.</p>

      <h2 className="font-serif text-2xl mt-8">2. Cookies we use</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Strictly necessary</strong> — required to run the Platform: session cookie for authentication, CSRF token, language preference (<code>rt_lang</code>). These cannot be disabled without breaking basic features.</li>
        <li><strong>Functional</strong> — remember your UI choices (e.g., which city filter you selected).</li>
        <li><strong>Analytics</strong> — help us understand which pages are used and where users struggle, using aggregated statistics only. Set only after you have accepted the cookie banner.</li>
        <li><strong>Payment</strong> — set by our payment processor (Stripe) when you go through checkout, to protect against fraud.</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">3. Managing cookies</h2>
      <p>You can accept or refuse non-essential cookies at any time through the cookie banner or your browser settings. Most browsers let you block or delete cookies; note that blocking strictly necessary cookies will prevent you from signing in or booking.</p>

      <h2 className="font-serif text-2xl mt-8">4. Third-party cookies</h2>
      <p>Some cookies are placed by third parties we use to operate the Platform, notably Stripe (payments), Google Fonts (Cormorant &amp; Manrope), and — where we serve maps — CDN providers (jsDelivr, Unsplash).</p>

      <h2 className="font-serif text-2xl mt-8">5. Do Not Track</h2>
      <p>Our Platform respects the "Do Not Track" signal by not setting analytics or marketing cookies when it is present.</p>

      <h2 className="font-serif text-2xl mt-8">6. Changes</h2>
      <p>We may update this Cookie Policy. The date at the top of this page reflects the latest revision.</p>
    </>
  );

  const FR = (
    <>
      <p>Cette Politique cookies explique comment <strong>Refine Touch</strong> utilise les cookies et technologies similaires sur la Plateforme.</p>

      <h2 className="font-serif text-2xl mt-8">1. Qu'est-ce qu'un cookie ?</h2>
      <p>Un cookie est un petit fichier texte placé sur votre appareil lors de la visite d'un site. Il aide le site à retenir certaines informations (session, langue) entre les pages et les visites.</p>

      <h2 className="font-serif text-2xl mt-8">2. Cookies utilisés</h2>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Strictement nécessaires</strong> — requis pour faire fonctionner la Plateforme : cookie de session, jeton CSRF, préférence de langue (<code>rt_lang</code>). Ils ne peuvent être désactivés sans casser les fonctions de base.</li>
        <li><strong>Fonctionnels</strong> — mémorisent vos choix d'interface (ex. filtre de ville sélectionné).</li>
        <li><strong>Analytiques</strong> — nous aident à comprendre l'utilisation via des statistiques agrégées. Déposés uniquement après votre acceptation du bandeau.</li>
        <li><strong>Paiement</strong> — déposés par notre prestataire (Stripe) lors du paiement, pour prévenir la fraude.</li>
      </ul>

      <h2 className="font-serif text-2xl mt-8">3. Gestion des cookies</h2>
      <p>Vous pouvez accepter ou refuser les cookies non essentiels à tout moment via le bandeau ou les réglages de votre navigateur. Notez que bloquer les cookies strictement nécessaires vous empêchera de vous connecter ou de réserver.</p>

      <h2 className="font-serif text-2xl mt-8">4. Cookies tiers</h2>
      <p>Certains cookies sont déposés par des tiers que nous utilisons : Stripe (paiements), Google Fonts (Cormorant &amp; Manrope) et, pour la carte, des CDN (jsDelivr, Unsplash).</p>

      <h2 className="font-serif text-2xl mt-8">5. Do Not Track</h2>
      <p>La Plateforme respecte le signal « Do Not Track » : lorsqu'il est présent, aucun cookie d'analyse ou marketing n'est déposé.</p>

      <h2 className="font-serif text-2xl mt-8">6. Modifications</h2>
      <p>Nous pouvons modifier cette Politique. La date en haut de page reflète la dernière révision.</p>
    </>
  );

  return <LegalShell title={t("legal_cookies")}>{lang === "fr" ? FR : EN}</LegalShell>;
}

/* -------------------- CANCELLATION & REFUND POLICY -------------------- */

export function RefundPage() {
  const { lang, t } = useI18n();
  useSEO({
    title: "Cancellation & Refund Policy | Refine Touch USA",
    description: "How cancellations and refunds work on Refine Touch USA — free cancellation up to 48 hours before your service.",
    canonical: `${SITE_URL}/refund-policy`,
    ogImage: OG_IMAGE,
  });
  const EN = (
    <>
      <p><strong>We want every booking to be a great experience.</strong> This policy explains how cancellations and refunds work when you book a Provider through Refine Touch USA. All amounts are in U.S. dollars.</p>
      <h2 className="font-serif text-2xl mt-8">1. Cancelling as a Customer</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>More than 48 hours before</strong> the scheduled service: free cancellation, 100% refund.</li>
        <li><strong>Between 24 and 48 hours before</strong>: 50% refund.</li>
        <li><strong>Less than 24 hours before, or no-show</strong>: no refund, as the Provider has reserved the time for you.</li>
      </ul>
      <h2 className="font-serif text-2xl mt-8">2. Cancellation by the Provider</h2>
      <p>If a Provider cancels for any reason, you receive a full refund within 5–10 business days and our team will help you find an alternative Provider.</p>
      <h2 className="font-serif text-2xl mt-8">3. Service not as described</h2>
      <p>If the service was not delivered, or materially differs from the listing, contact us within 72 hours of the scheduled date. We review each case individually and may issue a partial or full refund.</p>
      <h2 className="font-serif text-2xl mt-8">4. How refunds are issued</h2>
      <p>Refunds are returned to the original payment method. Platform fees are refunded together with the service price when a refund is granted.</p>
      <h2 className="font-serif text-2xl mt-8">5. Rescheduling</h2>
      <p>You can reschedule once free of charge up to 24 hours before the service, subject to the Provider's availability, directly from your dashboard.</p>
    </>
  );
  const FR = (
    <>
      <p><strong>Nous voulons que chaque réservation soit une réussite.</strong> Cette politique explique le fonctionnement des annulations et remboursements lorsque vous réservez un prestataire via Refine Touch USA. Tous les montants sont en dollars américains.</p>
      <h2 className="font-serif text-2xl mt-8">1. Annulation par le client</h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Plus de 48 h avant</strong> la prestation : annulation gratuite, remboursement à 100 %.</li>
        <li><strong>Entre 24 h et 48 h avant</strong> : remboursement à 50 %.</li>
        <li><strong>Moins de 24 h avant, ou absence</strong> : aucun remboursement, le prestataire ayant réservé ce créneau pour vous.</li>
      </ul>
      <h2 className="font-serif text-2xl mt-8">2. Annulation par le prestataire</h2>
      <p>Si un prestataire annule, vous êtes intégralement remboursé sous 5 à 10 jours ouvrés et notre équipe vous aide à trouver une alternative.</p>
      <h2 className="font-serif text-2xl mt-8">3. Prestation non conforme</h2>
      <p>Si la prestation n'a pas été réalisée ou diffère sensiblement de l'annonce, contactez-nous sous 72 h. Chaque cas est étudié individuellement et peut donner lieu à un remboursement partiel ou total.</p>
      <h2 className="font-serif text-2xl mt-8">4. Modalités de remboursement</h2>
      <p>Les remboursements sont effectués sur le moyen de paiement d'origine. Les frais de plateforme sont remboursés avec le prix de la prestation lorsqu'un remboursement est accordé.</p>
      <h2 className="font-serif text-2xl mt-8">5. Report</h2>
      <p>Vous pouvez reporter une fois gratuitement jusqu'à 24 h avant la prestation, selon les disponibilités du prestataire, depuis votre tableau de bord.</p>
    </>
  );
  return <LegalShell title={t("legal_refund")}>{lang === "fr" ? FR : EN}</LegalShell>;
}
